import {
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import moment from "moment";

import DialogForm from "../../shared/components/DialogForm";
import { IOSSwitch } from "./ReservationView"; // adjust path if this file moves
import { ApiEndPoints } from "../../apis/ApiEndPoints";
import {
  createRecord,
  fetchList,
  getRecord,
  updateCardRecord,
} from "../../apis/services/CommonApiService";
import { VerifyCard } from "../../apis/services/VerifyCard";
import { cryptoEncryptData } from "../../utils/helper";
import { getLoadingSuccess, getLoadingFailed } from "../../Reducer/authSlice";

/**
 * Fully self-contained "Make Payment" dialog.
 *
 * Usage — from anywhere in the app:
 *
 *   <MakePaymentDialog
 *     open={openMakePaymentDialog}
 *     onClose={() => setOpenMakePaymentDialog(false)}
 *     bookingId={booking.id}
 *     onSuccess={() => refreshWhateverList()}
 *   />
 *
 * Only `bookingId` is required — everything else (amount, user, existing
 * payment method, saved cards, pinpad devices) is fetched internally.
 * If the parent page already has the full booking object in memory, pass
 * it as `bookingDetail` to skip the extra GET_BOOKING_DETAILS call.
 */
const emptyCard = {
  id: 0,
  user_id: "",
  card_holder_name: "",
  card_number: "",
  yyy: "",
  mm: "",
  cvv: "",
  card_type: "",
};

const monthList = [
  "01", "02", "03", "04", "05", "06",
  "07", "08", "09", "10", "11", "12",
];

const MakePaymentDialog = ({
  open,
  onClose,
  bookingId,
  bookingDetail, // optional pre-fetched booking object, saves a network call
  onSuccess,     // called after a successful authorise/capture
  showNote = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  // Derived-from-booking state
  const [userId, setUserId] = useState("");
  const [finalAmount, setFinalAmount] = useState(0);
  const [isCashAllowed, setIsCashAllowed] = useState(0);

  // Payment method state
  const [paymentType, setPaymentType] = useState(2); // 1 = cash, 2 = card
  const [cardPresent, setCardPresent] = useState(false);
  const [card, setCard] = useState(emptyCard);
  const [cardDetail, setCardDetail] = useState([]);
  const [pinpadDevicesList, setPinpadDevicesList] = useState([]);
  const [selectPinpadDevices, setSelectPinpadDevices] = useState("0");
  const [suOn, setSuOn] = useState(false);
  const [suToken, setSuToken] = useState(null);

  // Year list for card expiry
  const [yearList, setYearList] = useState([]);
  useEffect(() => {
    const currentYear = Number(moment().format("YYYY"));
    setYearList(Array.from({ length: 30 }, (_, i) => String(currentYear + i)));
  }, []);

  // Transaction fields
  const [paymentFields, setPaymentFields] = useState({
    description: "",
    amount: 0,
  });

  const resetState = () => {
    setPaymentFields({ description: "", amount: 0 });
    setCard(emptyCard);
    setCardDetail([]);
    setCardPresent(false);
    setSelectPinpadDevices("0");
    setSuOn(false);
    setSuToken(null);
  };

  // ---------- Init on open ----------
  useEffect(() => {
    if (!open || !bookingId) return;
    initPaymentData();
  }, [open, bookingId]);

  const initPaymentData = async () => {
    setLoading(true);
    let detail = bookingDetail;

    if (!detail) {
      const result = await fetchList(
        ApiEndPoints.GET_BOOKING_DETAILS + `/${bookingId}`
      );
      detail = result?.status === 200 ? result.data : null;
    }

    if (detail) {
      setFinalAmount(detail?.final_amount || 0);
      setUserId(detail?.user_id);
      setIsCashAllowed(detail?.hotelData?.is_cash_allowed || 0);
      setPaymentType(detail?.payment_type || 2);
      setCardPresent(detail?.card_present === 1);
      setSelectPinpadDevices(detail?.terminal_id || "0");

      if (detail?.su_pg_id) {
        setSuToken(detail.su_pg_id);
        setSuOn(true);
      } else {
        setSuToken(null);
        setSuOn(false);
      }

      const existingCard = detail?.bookingPaymentDetails?.cardDetail;
      if (existingCard?.id) {
        setCard({
          id: existingCard.id,
          user_id: existingCard.user_id,
          card_holder_name: existingCard.card_holder_name,
          card_number: existingCard.card_number,
          yyy: existingCard.expiry_date?.split("-")[0],
          mm: existingCard.expiry_date?.split("-")[1],
          cvv: existingCard.cvv,
          card_type: existingCard.card_type,
        });
      }

      await fetchCards(detail?.user_id);
    }

    await fetchPinpadDevices();
    setLoading(false);
  };

  const fetchCards = async (uid) => {
    if (!uid) return;
    const result = await getRecord(uid, ApiEndPoints.GET_USER_CARD_DETAIL);
    if (result?.status === 200) {
      setCardDetail(
        (result.data || []).map((item) => ({
          id: item.id,
          user_id: item.user_id,
          card_holder_name: item.card_holder_name,
          card_number: item.card_number,
          expiry_date: item.expiry_date,
          cvv: item.cvv,
          card_type: item.card_type,
        }))
      );
    } else {
      setCardDetail([]);
    }
  };

  const fetchPinpadDevices = async () => {
    const result = await fetchList(ApiEndPoints.GET_ALL_PINPAD_DEVICE);
    if (result?.status === 200) {
      setPinpadDevicesList(result?.data || []);
    } else {
      setPinpadDevicesList([]);
    }
  };

  // ---------- Field handlers ----------
  const handleCardPresentToggle = (e) => {
    setCardPresent(e.target.checked);
    setCard(emptyCard);
  };

  const handleSelectCard = (e) => {
    const value = e.target.value;
    if (value === 0) {
      setCard(emptyCard);
      return;
    }
    const data = cardDetail.find((item) => item.id === value);
    setCard({
      id: Number.parseInt(data?.id),
      user_id: data?.user_id,
      card_holder_name: data?.card_holder_name,
      card_number: data?.card_number,
      yyy: data?.expiry_date?.split("-")[0],
      mm: data?.expiry_date?.split("-")[1],
      cvv: data?.cvv,
      card_type: data?.card_type,
    });
  };

  const handleCardFieldChange = (e) => {
    setCard((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePaymentFieldChange = (e) => {
    const { name, value } = e.target;
    setPaymentFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddCard = async () => {
    if (
      !card?.card_holder_name ||
      !card?.yyy ||
      !card?.card_number ||
      !card?.mm
    ) {
      toast.error(t("You Must Add Card Detail"));
      return;
    }
    if (card?.card_number?.length < 8) {
      toast.error(t("Please, enter valid card number"));
      return;
    }

    const name = card.card_holder_name.trim().split(/\s+/);
    const expiryParts = card.yyy.split("");

    const bodyFormData = {
      user_id: userId,
      card_number: cryptoEncryptData(
        Number.parseInt(card.card_number).toString()
      ),
      expiry_date: cryptoEncryptData(
        `${card.mm}${expiryParts.at(-2)}${expiryParts.at(-1)}`.toString()
      ),
      first_name: name[0],
      last_name: name[1] || "",
      security_code: cryptoEncryptData(card.cvv?.toString()),
    };

    setLoading(true);
    const result = await VerifyCard(bodyFormData, card);
    if (result?.status === 200) {
      const verifiedBody = result?.bodyFormData;
      const addResult = await createRecord(
        verifiedBody,
        ApiEndPoints.ADD_USER_CARD_DETAIL
      );
      if (addResult?.status === 200) {
        toast.success(addResult.message);
        if (cardDetail.length === 0 && bookingId) {
          await updateCardRecord(
            {
              booking_id: bookingId,
              card_id: Number.parseInt(addResult?.data?.id),
            },
            ApiEndPoints.CHANGE_PRIMARY_CARD
          );
        }
        await fetchCards(userId);
      } else {
        toast.error(addResult?.message);
      }
    } else {
      toast.error(result?.message);
    }
    setLoading(false);
  };

  // ---------- Authorize / Capture ----------
  const validateBeforeSubmit = () => {
    if (!paymentFields.description) {
      toast.error(t("Please Enter Payment Name"));
      return false;
    }
    if (+paymentFields.amount <= 0) {
      toast.error(t("Please Enter Amount"));
      return false;
    }
    if (cardPresent === false && paymentType === 2 && card?.id === 0 && !suOn) {
      toast.error(t("Please Select Card or Add Card"));
      return false;
    }
    return true;
  };

  const getRemainingAmount = async () => {
    const amountResult = await getRecord(
      bookingId,
      ApiEndPoints.CHECK_AMOUNT_CAPTURE_AND_AUTHORISE
    );
    if (amountResult?.status !== 200) {
      toast.error(amountResult?.message);
      return null;
    }
    return +finalAmount - +amountResult?.data?.amount;
  };

  const buildBasePayload = () => ({
    booking_id: bookingId,
    credit_card_id:
      (cardPresent === true && paymentType === 2) || paymentType === 1
        ? "0"
        : card?.id,
    amount: paymentFields.amount,
    description: paymentFields.description,
    card_present: cardPresent === true ? 1 : 0,
    device_id: selectPinpadDevices,
    ...(!cardPresent && suOn ? { su_token: suToken } : {}),
  });

  const handleAuthorise = async () => {
    if (!validateBeforeSubmit()) return;
    dispatch(getLoadingSuccess());
    setLoading(true);

    const remaining = await getRemainingAmount();
    if (remaining === null) {
      dispatch(getLoadingFailed());
      setLoading(false);
      return;
    }

    if (+remaining.toFixed(2) >= +paymentFields.amount) {
      const result = await createRecord(
        buildBasePayload(),
        ApiEndPoints.PAYMENT_CARD_AUTHORIZATION
      );
      if (result.status === 200) {
        toast.success(result?.message);
        handleClose();
        onSuccess && onSuccess();
      } else {
        toast.error(result?.message);
      }
    } else {
      toast.error(
        `${t("Sorry,Your Remaining Amount is")} ${remaining.toFixed(2)}`
      );
    }
    dispatch(getLoadingFailed());
    setLoading(false);
  };

  const handleCapture = async () => {
    if (!validateBeforeSubmit()) return;
    dispatch(getLoadingSuccess());
    setLoading(true);

    const remaining = await getRemainingAmount();
    if (remaining === null) {
      dispatch(getLoadingFailed());
      setLoading(false);
      return;
    }

    if (+remaining.toFixed(2) >= +paymentFields.amount) {
      const result = await createRecord(
        { ...buildBasePayload(), user_id: userId, payment_type: paymentType },
        ApiEndPoints.PAYMENT_CARD_CAPTURE
      );
      if (result.status === 200) {
        toast.success(result?.message);
        handleClose();
        onSuccess && onSuccess();
      } else {
        toast.error(result?.message);
      }
    } else {
      toast.error(
        `${t("Sorry,Your Remaining Amount is")} ${remaining.toFixed(2)}`
      );
    }
    dispatch(getLoadingFailed());
    setLoading(false);
  };

  const handleClose = () => {
    resetState();
    onClose && onClose();
  };

  return (
    <DialogForm
      scroll="paper"
      maxWidth="lg"
      title={t("Make Payment")}
      openDialog={open}
      handleDialogClose={handleClose}
      bodyContent={
        <div className="p-2">
          {showNote && (
            <div className="px-2 text-center text-sm">
              <strong>{t("Note")}:</strong>{" "}
              {t("The remaining amount will be deducted from the primary card.")}
            </div>
          )}

          <Grid container spacing={2} alignItems="flex-start">
            {/* Payment Type */}
            <Grid item xs={12} lg={3} className="mt-2">
              <Typography variant="h4" style={{ fontSize: "16px", marginBottom: 11 }}>
                {t("Payment Option")}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>{t("Payment Type")}</InputLabel>
                <Select
                  value={paymentType}
                  label={t("Payment Type")}
                  onChange={(e) => setPaymentType(e.target.value)}
                >
                  {isCashAllowed === 1 && (
                    <MenuItem value={1}>{t("Cash")}</MenuItem>
                  )}
                  <MenuItem value={2}>{t("Credit Card")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Card Present toggle */}
            {paymentType === 2 && (
              <Grid item xs={12} lg={3} className="mt-2">
                <Typography variant="h4" style={{ fontSize: "16px", marginBottom: 11 }}>
                  {t("Card Present")}
                </Typography>
                <FormControlLabel
                  control={
                    <IOSSwitch
                      sx={{ m: 1 }}
                      checked={cardPresent}
                      onChange={handleCardPresentToggle}
                    />
                  }
                  label=""
                />
              </Grid>
            )}

            {/* Card entry (not present) */}
            {cardPresent === false && !suOn && paymentType === 2 && (
              <Grid item xs={12}>
                <div
                  style={{
                    border: "2px solid #B4ADAD",
                    borderRadius: "5px",
                    padding: "20px",
                    marginTop: "25px",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={2}>
                      <Typography id="font-600">{t("Select Card")}</Typography>
                      <FormControl fullWidth>
                        <InputLabel>{t("Select Card")}</InputLabel>
                        <Select
                          value={card?.id}
                          label={t("Select Card")}
                          onChange={handleSelectCard}
                        >
                          <MenuItem value={0}>{t("Add Card")}</MenuItem>
                          {cardDetail.map((item) => (
                            <MenuItem value={item.id} key={item.id}>
                              {item.card_holder_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <Typography id="font-600">{t("Card Holder Name")}</Typography>
                      <TextField
                        fullWidth
                        value={card?.card_holder_name}
                        disabled={!(card?.id === 0 || Number.isNaN(card?.id))}
                        placeholder={t("Card Holder Name")}
                        onChange={handleCardFieldChange}
                        name="card_holder_name"
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <Typography id="font-600">{t("Card Number")}</Typography>
                      <TextField
                        fullWidth
                        value={card?.card_number}
                        placeholder={t("Card Number")}
                        disabled={!(card?.id === 0 || Number.isNaN(card?.id))}
                        type="text"
                        inputMode="numeric"
                        name="card_number"
                        onChange={(e) => {
                          const value = e.target.value.replaceAll(/\D/g, "");
                          if (value.length > 16) return;
                          handleCardFieldChange({
                            target: { name: "card_number", value },
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} lg={1}>
                      <Typography id="font-600">{t("Expiry")}</Typography>
                      <FormControl fullWidth>
                        <InputLabel>{"MM"}</InputLabel>
                        <Select
                          name="mm"
                          input={<OutlinedInput label="MM" />}
                          disabled={!(card?.id === 0 || Number.isNaN(card?.id))}
                          onChange={handleCardFieldChange}
                          value={card?.mm + ""}
                        >
                          <MenuItem value="">{t("MM")}</MenuItem>
                          {monthList.map((m) => (
                            <MenuItem key={m} value={m}>{m}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} lg={1}>
                      <Typography>&nbsp;</Typography>
                      <FormControl fullWidth>
                        <InputLabel>{"YYYY"}</InputLabel>
                        <Select
                          name="yyy"
                          input={<OutlinedInput label="YYYY" />}
                          disabled={!(card?.id === 0 || Number.isNaN(card?.id))}
                          onChange={handleCardFieldChange}
                          value={card?.yyy + ""}
                        >
                          <MenuItem value="">{t("YYYY")}</MenuItem>
                          {yearList.map((y) => (
                            <MenuItem key={y} value={y}>{y}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {card?.id === 0 || Number.isNaN(card?.id) ? (
                      <Grid item xs={6} lg={1.8}>
                        <Typography id="font-600">{t("Security Code")}</Typography>
                        <TextField
                          fullWidth
                          value={card?.cvv}
                          onChange={handleCardFieldChange}
                          placeholder={t("CVV")}
                          name="cvv"
                          type="number"
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={6} lg={1.8}>
                        <Typography id="font-600">{t("Card Type")}</Typography>
                        <TextField fullWidth value={card?.card_type} disabled />
                      </Grid>
                    )}
                    {(card?.id === 0 || Number.isNaN(card?.id)) && (
                      <Grid item xs={12} lg={1.5}>
                        <Typography>&nbsp;</Typography>
                        <Button
                          variant="contained"
                          onClick={handleAddCard}
                          className="comman-button"
                        >
                          {t("Add & Verify")}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </div>
              </Grid>
            )}

            {/* Pinpad device (card present) */}
            {cardPresent === true && paymentType === 2 && (
              <Grid item xs={12}>
                <div
                  style={{
                    border: "2px solid #B4ADAD",
                    borderRadius: "5px",
                    padding: "20px",
                    marginTop: "25px",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={4}>
                      <Typography id="font-600">{t("Pinpad Devices")}</Typography>
                      <FormControl fullWidth>
                        <Select
                          value={selectPinpadDevices}
                          onChange={(e) => setSelectPinpadDevices(e.target.value)}
                        >
                          <MenuItem value={0}>{t("-- select --")}</MenuItem>
                          {pinpadDevicesList.map((item) => (
                            <MenuItem value={item.device_id} key={item.id}>
                              {item.device_id}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </div>
              </Grid>
            )}

            {/* Virtual card token */}
            {!cardPresent && suToken && paymentType === 2 && (
              <>
                <Grid item xs={12} lg={2}>
                  <Typography id="font-600">{t("Virtual card token")}</Typography>
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        sx={{ m: 1 }}
                        checked={suOn}
                        onChange={(e) => setSuOn(e.target.checked)}
                      />
                    }
                    label=""
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Typography id="font-600">{t("Token Number")}</Typography>
                  <TextField
                    fullWidth
                    value={String(suToken?.substr(suToken?.length - 4)).padStart(12, "x")}
                    disabled
                  />
                </Grid>
              </>
            )}

            {/* Transaction description + amount */}
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12} md={6}>
                <h6 className="mb-1">
                  {t("Enter Transaction Name")} <span style={{ color: "red" }}>*</span>
                </h6>
                <TextField
                  fullWidth
                  name="description"
                  value={paymentFields.description}
                  onChange={handlePaymentFieldChange}
                  variant="outlined"
                  placeholder={t("Enter Transaction Name")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <h6 className="mb-1">
                  {t("Amount")} <span style={{ color: "red" }}>*</span>
                </h6>
                <TextField
                  fullWidth
                  name="amount"
                  value={paymentFields.amount}
                  onChange={handlePaymentFieldChange}
                  variant="outlined"
                  type="number"
                  placeholder={t("Enter Amount")}
                />
              </Grid>
            </Grid>

            {/* Actions */}
            <Grid item xs={12} lg={4}>
              <div className="d-flex align-items-center">
                {paymentType === 2 && (
                  <Button
                    className="mt-1 mr-2 comman-button"
                    size="large"
                    variant="contained"
                    onClick={handleAuthorise}
                    disabled={loading}
                    color="primary"
                  >
                    {t("Authorise")}
                  </Button>
                )}
                <Button
                  className="mt-1 comman-button"
                  size="large"
                  variant="contained"
                  onClick={handleCapture}
                  disabled={loading}
                  color="primary"
                >
                  {t("Direct Payment")}
                </Button>
              </div>
            </Grid>
          </Grid>
        </div>
      }
    />
  );
};

export default MakePaymentDialog;
